/**
 * Utility functions for formatting company addresses consistently across the application
 */

export interface AddressData {
  address?: string;
  country?: string;
}

/**
 * Formats address for single-line display (tables, cards, dropdowns)
 * Example: "123 Main St, Los Angeles, CA 90210, United States"
 */
export const formatAddressSingleLine = (addressData: AddressData): string => {
  if (!addressData.address && !addressData.country) return '';
  
  const parts: string[] = [];
  
  if (addressData.address) {
    // Convert line breaks to commas and clean up extra spaces
    const cleanAddress = addressData.address
      .replace(/\n+/g, ', ')
      .replace(/,\s*,/g, ',')
      .replace(/,\s+/g, ', ')
      .trim();
    parts.push(cleanAddress);
  }
  
  if (addressData.country) {
    parts.push(addressData.country);
  }
  
  return parts.join(', ');
};

/**
 * Formats address for multi-line display (detailed views, forms, documents)
 * Preserves line breaks and adds country on separate line
 */
export const formatAddressMultiLine = (addressData: AddressData): string => {
  if (!addressData.address && !addressData.country) return '';
  
  const parts: string[] = [];
  
  if (addressData.address) {
    parts.push(addressData.address.trim());
  }
  
  if (addressData.country) {
    parts.push(addressData.country);
  }
  
  return parts.join('\n');
};

/**
 * Formats address for compact display (limited space contexts)
 * Shows only the most essential parts
 */
export const formatAddressCompact = (addressData: AddressData, maxLength: number = 50): string => {
  const singleLine = formatAddressSingleLine(addressData);
  
  if (singleLine.length <= maxLength) {
    return singleLine;
  }
  
  // Try to show address without country if it's too long
  if (addressData.address) {
    const addressOnly = addressData.address.replace(/\n+/g, ', ').trim();
    if (addressOnly.length <= maxLength) {
      return addressOnly;
    }
    
    // Truncate and add ellipsis
    return addressOnly.substring(0, maxLength - 3) + '...';
  }
  
  return singleLine.substring(0, maxLength - 3) + '...';
};

/**
 * Gets the city from an address string (best effort)
 * Useful for sorting or filtering by city
 */
export const extractCity = (address?: string): string => {
  if (!address) return '';
  
  // Split by commas and try to find what looks like a city
  const parts = address.split(',').map(p => p.trim());
  
  // For most formats, city is often the second-to-last or last substantial part
  // This is a best-effort extraction
  if (parts.length >= 2) {
    // Skip parts that look like postal codes (numbers/letters only)
    const cityCandidate = parts.find(part => 
      part.length > 2 && 
      !/^\d+$/.test(part) && 
      !/^[A-Z]{2,3}\s*\d/.test(part)
    );
    return cityCandidate || parts[0] || '';
  }
  
  return parts[0] || '';
};

/**
 * Checks if address data has any content
 */
export const hasAddressData = (addressData: AddressData): boolean => {
  return Boolean(addressData.address?.trim() || addressData.country?.trim());
};